// @flow

import * as React from 'react';

import shallowequal from 'shallowequal';

import { serializeQuery } from '@gandi/app-runner';
import type { Currency } from '@gandi/billing';
import type { Contract } from '@gandi/contracts';
import { AddressField, PhoneField } from '@gandi/react-places';
import type { OrganizationType } from '@gandi/redux-organization';
import type { Organization } from '@gandi/redux-sharing';
import type { TranslatorProps } from '@gandi/translate';
import {
  Base,
  Spinner,
  Alert,
  TogglePill,
  ToggleGroup,
  Button,
} from '@gandi/ui';

import {
  CheckboxField,
  Fieldset,
  Form,
  InputTextField,
  Label,
  SelectField,
  type FormComponentProps,
} from 'gandi.react-form';

import type { WithRouterProps } from 'src/helpers/withRouter';

import styles from './AccountForm.scss';

export type StateProps = {
  hasVat: boolean,
  isCountryFR: boolean,
  type: string,
  userCountryCode: string,
};

type InjectedProps = {
  ...WithRouterProps<>,
  translator: TranslatorProps,

  // `gandi.react-form/FormComponentProps`
  change: FormComponentProps['change'],
  error: ?FormComponentProps['error'],
  form: FormComponentProps['form'],
  handleSubmit: (event: SyntheticEvent<>) => mixed,
  invalid: FormComponentProps['invalid'],
  submitFailed: FormComponentProps['submitFailed'],
  submitting: FormComponentProps['submitting'],
};

export type InitialValuesProps = Partial<{
  ...Organization,
  currency: ?Currency,
  ...
}>;

export type OwnProps = {
  cartId: string,
  hasOwnerOnly: boolean,
  canChangeCurrency: boolean,
  bu?: number,
  contracts?: Array<Contract>,
  currencies: Array<Currency>,
  isCreation?: boolean,
  isBuyingForMyself?: boolean,
  onTypeChange?: () => void,

  // `gandi.react-form/FormConfig`
  initialValues: Partial<InitialValuesProps>,
  /* eslint-disable react/no-unused-prop-types */
  destroyOnUnmount?: boolean,
  forceUnregisterOnUnmount?: boolean,
  onSubmit: (event: any) => Promise<any>,
  /* eslint-enable react/no-unused-prop-types */
};

export type AccountFormProps = {
  ...OwnProps,
  ...StateProps,
  ...InjectedProps,
};

type TypeItem = {
  value: string,
  label: string,
  text?: string,
};

type State = {
  isLoading: boolean,
};

const selectVariant = ['block'];
export default class AccountForm extends React.Component<
  AccountFormProps,
  State,
> {
  state: State = { isLoading: false };
  typeItems: Array<TypeItem>;

  static FORM_ID: string = 'accountForm';

  constructor(props: AccountFormProps) {
    super(props);
    const { __ } = this.props.translator;

    this.typeItems = [
      { value: 'company', label: __('Company'), text: __('Company') },
      {
        value: 'association',
        label: __('Association'),
        text: __('Association'),
      },
      {
        value: 'publicbody',
        label: __('Public body'),
        text: __('Public body'),
      },
    ];

    if (props.searchParams.has('first')) {
      this.typeItems.unshift({
        value: 'individual',
        label: __('Individual'),
        text: __('Individual'),
      });
    }
  }

  componentDidMount() {
    this.props.change('type', this.props.type);
  }

  componentDidUpdate(prevProps: AccountFormProps) {
    // If we're confident the currencies selector are always properly memoized
    // and fallback to a ever-same empty array ref, we could strip away
    // the shallow-eqality check and only rely on a strict equality one.
    if (!shallowequal(prevProps.currencies, this.props.currencies)) {
      this.props.change('currency', this.props.currencies[0]);
    }
  }

  onChangeCountry: (evt: SyntheticInputEvent<>) => void = (
    evt: SyntheticInputEvent<>,
  ) => {
    const country = evt.target.value;

    if (this.props.isCreation) {
      this.props.change('currency', '');
    }

    if (country !== 'FR') {
      this.props.change('siren', '');
    }

    if (!this.props.hasVat) {
      this.props.change('vat_number', '');
    }
  };

  handleTypePillChange: (evt: SyntheticInputEvent<>) => void = (
    evt: SyntheticInputEvent<>,
  ) => {
    if (this.props.onTypeChange) {
      this.props.onTypeChange();
    }

    const { type, cartId } = this.props;

    const target = evt.target;
    // $FlowFixMe[incompatible-type]: assume the value from form is valid
    const value: OrganizationType = target.value;

    if (value === type) {
      return;
    }

    this.props.change('type', value);

    if (type !== 'individual' && value === 'individual') {
      this.props.navigate({
        pathname: `/cart/${cartId}/configure/account/myself`,
        search: serializeQuery({
          first: 1,
        }),
      });

      return;
    }

    if (value !== 'individual' && type === 'individual') {
      this.props.setSearchParams(prev => {
        prev.set('first', '1');
        return prev;
      });

      return;
    }

    const searchParams = this.props.searchParams;
    searchParams.set('type', value);

    this.props.navigate({
      pathname: `/cart/${cartId}/configure/account/new`,
      search: searchParams.toString(),
    });
  };

  renderTypePill: (typeItem: TypeItem) => React.Node = (typeItem: TypeItem) => (
    <TogglePill id={typeItem.value} key={typeItem.value} value={typeItem.value}>
      {typeItem.label}
    </TogglePill>
  );

  renderContractCheckbox: (
    contract: Contract,
    index: number,
  ) => React.MixedElement = (contract, index) => (
    <CheckboxField
      key={`${contract.identifier}-${contract.version}`}
      name={`contract${index}`}
    >
      <span>{contract.description}</span>
      {!!contract.href && (
        <a
          className={styles.link}
          target="_blank"
          rel="noopener noreferrer"
          href={contract.href}
        >
          {this.props.translator.__('See')}
        </a>
      )}
    </CheckboxField>
  );

  renderContracts(): React.Node {
    if (!Array.isArray(this.props.contracts)) {
      return null;
    }

    return this.props.contracts.map(this.renderContractCheckbox);
  }

  renderCurrency(): React.Node {
    const { __ } = this.props.translator;

    const disabled =
      !Array.isArray(this.props.currencies) ||
      !this.props.currencies.length ||
      !this.props.canChangeCurrency;

    const values = Array.isArray(this.props.currencies)
      ? this.props.currencies.map(currency => ({
          text: currency,
          value: currency,
        }))
      : [];

    return (
      <SelectField
        disabled={disabled}
        label={__('Currency')}
        name="currency"
        placeholder={__('Select an item...')}
        required
        values={values}
        variant={selectVariant}
      />
    );
  }

  render(): React.MixedElement {
    const {
      hasOwnerOnly,
      isBuyingForMyself,
      isCreation,
      type,
      cartId,
      submitting,
    } = this.props;

    const { __ } = this.props.translator;
    const isIndividual = type === 'individual';

    return (
      <div>
        {this.state.isLoading && <Spinner centered />}

        {!this.state.isLoading && (
          <div className="form-create form-centered">
            {this.props.submitFailed && this.props.invalid && (
              <Base marginBottom="base">
                <Alert
                  intent="danger"
                  title={__('Submission error')}
                  message={__('Please correct the errors below to continue.')}
                />
              </Base>
            )}

            {this.props.error && (
              <Base marginBottom="base">
                <Alert
                  message={this.props.error}
                  title={__('Error')}
                  intent="danger"
                />
              </Base>
            )}

            {this.props.searchParams.has('first') && (
              <Base marginBottom="base">
                <Alert
                  intent="info"
                  message={__(
                    'Please complete the form below, adding any missing ' +
                      'information as required. This information is ' +
                      'necessary to complete your order.',
                  )}
                />
              </Base>
            )}

            {hasOwnerOnly && (
              <Base marginBottom="base">
                <Alert
                  intent="info"
                  message={__(
                    'This shopping cart contains products that can only be invoiced to' +
                      ' their legal owner. You cannot change the invoice recipient.',
                  )}
                />
              </Base>
            )}
            <Form
              className="form-stacked wrapper-plain"
              role="form"
              id={AccountForm.FORM_ID}
              method="post"
              noValidate
              onSubmit={this.props.handleSubmit}
            >
              {(this.props.searchParams.has('first') || isCreation) && (
                <Fieldset>
                  <Label target="type" text={__('Type')} required />

                  {/* FIXME: react-form wrapper */}
                  <ToggleGroup
                    id="type"
                    type="radio"
                    name="type"
                    value={this.props.type}
                    onChange={this.handleTypePillChange}
                  >
                    {this.typeItems.map(this.renderTypePill)}
                  </ToggleGroup>
                </Fieldset>
              )}

              {!isIndividual && !isBuyingForMyself && (
                <InputTextField
                  help={__(
                    "This is the name that will be displayed on Gandi's website" +
                      ' to referer to your organization.',
                  )}
                  label={__('Organization display name')}
                  name="name"
                  required
                />
              )}
              {!isIndividual && (
                <InputTextField
                  disabled={!!this.props.initialValues.orgname}
                  help={__(
                    'This is the full real name of your organization. It will' +
                      ' be used on invoices and in every legal aspect of your experience at Gandi.',
                  )}
                  label={__('Company name')}
                  name="orgname"
                  required
                />
              )}
              {!isIndividual && !isCreation && (
                <SelectField
                  className={styles.select}
                  disabled
                  label={__('Type')}
                  name="type"
                  value={type}
                  values={this.typeItems}
                  variant={selectVariant}
                />
              )}
              <InputTextField
                disabled={isIndividual && this.props.initialValues.firstname}
                label={__('First name')}
                name="firstname"
                required
              />
              <InputTextField
                disabled={isIndividual && this.props.initialValues.lastname}
                label={__('Last name')}
                name="lastname"
                required
              />
              <AddressField
                forceMandatorySubdivision
                defaultCountry={this.props.userCountryCode}
                onChangeCountry={this.onChangeCountry}
                change={this.props.change}
                form={this.props.form}
                bu={this.props.bu}
              />
              <InputTextField
                label={__('Email')}
                help={
                  !isCreation &&
                  (isBuyingForMyself
                    ? __(
                        'This is the email we will use to contact you in most cases. ' +
                          'You may also want to review your security email in your account settings.',
                      )
                    : __(
                        'This is the email we will use in most cases to contact this organization. ' +
                          'You may also want to review your own security email in your account settings.',
                      ))
                }
                name="email"
                required
                type="email"
              />
              <PhoneField required />
              <PhoneField label={__('Fax')} name="fax" />
              {this.renderCurrency()}
              {!isIndividual && this.props.isCountryFR && (
                <InputTextField label={__('Siren / Company ID')} name="siren" />
              )}
              {!isIndividual && (
                <InputTextField
                  disabled={!!this.props.initialValues.vat_number}
                  help={
                    this.props.initialValues.vat_number
                      ? __(
                          'If you have to change your VAT number, please contact our customer ' +
                            'service.',
                        )
                      : __(
                          'IMPORTANT: Enter your VAT number with your country code, and without ' +
                            'any spaces. For example: XX999999999',
                        )
                  }
                  label={__('Vat / Tax Number')}
                  name="vat_number"
                />
              )}
              {this.renderContracts()}
            </Form>

            <Base marginBottom="base" marginY="base">
              {!this.props.searchParams.has('first') && (
                <Button
                  type="button"
                  to={`/cart/${cartId}/configure/account-choice?guess`}
                  kind="outline"
                  className={styles.button}
                  grouped
                >
                  {__('Back')}
                </Button>
              )}
              <Button
                type="submit"
                form={AccountForm.FORM_ID}
                className={styles.button}
                loading={submitting}
              >
                {__('Continue')}
              </Button>
            </Base>
          </div>
        )}
      </div>
    );
  }
}
